/**
 * NotificationCenter — dropdown panel triggered by a bell icon.
 *
 * Renders as a Radix Popover (compact, 380px dropdown) or a Radix Dialog
 * (expanded full-screen modal, 720px centered) depending on state.
 *
 * Phase 1: local state with mock data via `useNotifications` hook.
 *
 * Wire into AppShell: place <NotificationBell /> between Slack status and user avatar
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Bell,
  CheckCircle,
  AlertTriangle,
  ListChecks,
  AlertCircle,
  GitBranch,
  Shield,
  Brain,
  Info,
  BellOff,
  X,
  ArrowLeft,
} from 'lucide-react';
import {
  useNotifications,
  formatRelativeTime,
  getTimeGroup,
  type Notification,
  type NotificationType,
  type NotificationAction,
} from '@/hooks/useNotifications';

/* ─── Icon resolver ─────────────────────────────────────────────────────── */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  CheckCircle,
  AlertTriangle,
  ListChecks,
  AlertCircle,
  GitBranch,
  Shield,
  Brain,
  Info,
};

function NotificationIcon({ iconName, color }: { iconName: string; color: string }): JSX.Element {
  const IconComponent = ICON_MAP[iconName] ?? Info;
  return <IconComponent size={14} className="shrink-0" style={{ color }} />;
}

/* ─── Filter tabs ───────────────────────────────────────────────────────── */

type FilterTab = 'all' | 'tasks' | 'agents' | 'system';

const FILTER_TABS: readonly { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'agents', label: 'Agents' },
  { key: 'system', label: 'System' },
];

const FILTER_TYPE_MAP: Record<FilterTab, readonly NotificationType[] | null> = {
  all: null,
  tasks: ['task', 'workflow', 'pipeline'],
  agents: ['agent', 'memory'],
  system: ['system', 'connection', 'security'],
};

/* ─── Action pill button ───────────────────────────────────────────────── */

function getActionPillClasses(action: NotificationAction): string {
  if (action.type === 'dismiss') {
    return 'text-wayne-ink-3 hover:text-wayne-ink';
  }
  if (action.type === 'action') {
    // Operational actions like reauth, resync, retry
    return 'text-wayne-accent border border-wayne-hairline hover:border-wayne-accent hover:bg-wayne-surface-2';
  }
  // navigate actions
  if (action.label.startsWith('Start a chat')) {
    return 'text-wayne-accent border border-wayne-accent/40 hover:border-wayne-accent hover:bg-wayne-surface-2';
  }
  return 'text-wayne-ink-2 border border-wayne-hairline hover:border-wayne-accent hover:text-wayne-accent hover:bg-wayne-surface-2';
}

interface ActionPillProps {
  action: NotificationAction;
  onExecute: () => void;
}

function ActionPill({ action, onExecute }: ActionPillProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onExecute();
      }}
      className={[
        'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium transition-all active:scale-[0.97]',
        getActionPillClasses(action),
      ].join(' ')}
      style={{ transition: 'all 150ms var(--ease-default)' }}
    >
      {action.label}
    </button>
  );
}

/* ─── Notification row ──────────────────────────────────────────────────── */

interface NotificationRowProps {
  notification: Notification;
  expanded: boolean;
  onMarkRead: (id: string) => void;
  onExecuteAction: (notificationId: string, action: NotificationAction) => void;
  onNavigate: (route: string) => void;
}

function NotificationRow({
  notification,
  expanded,
  onMarkRead,
  onExecuteAction,
  onNavigate,
}: NotificationRowProps): JSX.Element {
  const handleClick = () => {
    onMarkRead(notification.id);
    // Navigate to the first navigate action's route if available
    const navAction = notification.actions.find((a) => a.type === 'navigate');
    if (navAction?.route) {
      onNavigate(navAction.route);
    }
  };

  const handleActionExecute = (action: NotificationAction) => {
    onExecuteAction(notification.id, action);
    if (action.type === 'navigate' && action.route) {
      onNavigate(action.route);
    }
  };

  // Primary action = first non-dismiss action (shown on hover in compact mode)
  const primaryAction = notification.actions.find((a) => a.type !== 'dismiss');

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-wayne-surface-2 active:scale-[0.99] transition-all group"
      style={{ transition: 'background-color 150ms var(--ease-default), transform 80ms linear' }}
    >
      {/* Unread dot */}
      <span className="shrink-0 mt-1.5 flex items-center justify-center w-2 h-2">
        {!notification.read ? (
          <span
            className="block w-2 h-2 rounded-full"
            style={{ backgroundColor: 'var(--wayne-accent)' }}
          />
        ) : null}
      </span>

      {/* Icon */}
      <span className="shrink-0 mt-0.5">
        <NotificationIcon iconName={notification.icon} color={notification.color} />
      </span>

      {/* Content */}
      <span className="flex-1 min-w-0">
        <span
          className={[
            'block text-[13px] leading-tight',
            notification.read ? 'font-normal text-wayne-ink-2' : 'font-medium text-wayne-ink',
          ].join(' ')}
        >
          {notification.title}
        </span>
        <span className="block text-[12px] text-wayne-ink-3 mt-0.5 line-clamp-1 leading-snug">
          {notification.description}
        </span>
        <span className="block text-[11px] text-wayne-ink-3 font-mono mt-1">
          {formatRelativeTime(notification.timestamp)}
        </span>

        {/* Expanded mode: show all actions */}
        {expanded ? (
          <span className="flex flex-wrap gap-1.5 mt-2">
            {notification.actions.map((action) => (
              <ActionPill
                key={action.label}
                action={action}
                onExecute={() => handleActionExecute(action)}
              />
            ))}
          </span>
        ) : null}
      </span>

      {/* Compact mode: show primary action on hover */}
      {!expanded && primaryAction ? (
        <span className="shrink-0 mt-0.5">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ transition: 'opacity 150ms var(--ease-default)' }}>
            <ActionPill
              action={primaryAction}
              onExecute={() => handleActionExecute(primaryAction)}
            />
          </span>
        </span>
      ) : null}
    </button>
  );
}

/* ─── Empty state ───────────────────────────────────────────────────────── */

function EmptyState(): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <span className="w-12 h-12 rounded-full bg-wayne-surface-2 flex items-center justify-center mb-3">
        <BellOff size={20} className="text-wayne-ink-3" />
      </span>
      <p className="text-[14px] font-medium text-wayne-ink mb-1">
        You&apos;re all caught up
      </p>
      <p className="text-[12px] text-wayne-ink-3 max-w-[260px] leading-relaxed">
        Notifications appear here when agents complete tasks, workflows run, or things need your attention.
      </p>
    </div>
  );
}

/* ─── Time-grouped notification list ───────────────────────────────────── */

interface GroupedListProps {
  notifications: Notification[];
  expanded: boolean;
  onMarkRead: (id: string) => void;
  onExecuteAction: (notificationId: string, action: NotificationAction) => void;
  onNavigate: (route: string) => void;
}

function GroupedNotificationList({
  notifications,
  expanded,
  onMarkRead,
  onExecuteAction,
  onNavigate,
}: GroupedListProps): JSX.Element {
  if (notifications.length === 0) {
    return <EmptyState />;
  }

  if (!expanded) {
    // Compact mode: flat list, no grouping
    return (
      <div className="py-1">
        {notifications.map((notification) => (
          <NotificationRow
            key={notification.id}
            notification={notification}
            expanded={false}
            onMarkRead={onMarkRead}
            onExecuteAction={onExecuteAction}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    );
  }

  // Expanded mode: group by time
  const groups: { label: string; items: Notification[] }[] = [];
  const groupMap = new Map<string, Notification[]>();

  for (const n of notifications) {
    const label = getTimeGroup(n.timestamp);
    const existing = groupMap.get(label);
    if (existing) {
      existing.push(n);
    } else {
      const items = [n];
      groupMap.set(label, items);
      groups.push({ label, items });
    }
  }

  return (
    <div className="py-1">
      {groups.map((group) => (
        <div key={group.label}>
          <div className="px-4 pt-3 pb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-wayne-ink-3">
              {group.label}
            </span>
          </div>
          {group.items.map((notification) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              expanded={true}
              onMarkRead={onMarkRead}
              onExecuteAction={onExecuteAction}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ─── Filter bar (shared between compact and expanded) ─────────────────── */

interface FilterBarProps {
  activeFilter: FilterTab;
  onFilterChange: (tab: FilterTab) => void;
}

function FilterBar({ activeFilter, onFilterChange }: FilterBarProps): JSX.Element {
  return (
    <div className="flex gap-1">
      {FILTER_TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onFilterChange(tab.key)}
          className={[
            'px-2.5 py-1 text-[11px] font-medium transition-all active:scale-[0.97] border-b-2',
            activeFilter === tab.key
              ? 'border-wayne-accent text-wayne-accent'
              : 'border-transparent text-wayne-ink-3 hover:text-wayne-ink hover:border-wayne-ink-3',
          ].join(' ')}
          style={{ transition: 'border-color 150ms var(--ease-default), color 150ms var(--ease-default)' }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* ─── NotificationBell (exported for AppShell integration) ──────────────── */

/**
 * Standalone notification bell button + popover panel.
 * Supports compact (popover) and expanded (full-screen modal) modes.
 *
 * Wire into AppShell: place <NotificationBell /> between Slack status and user avatar
 */
export function NotificationBell(): JSX.Element {
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead, dismiss, clearAllRead, executeAction } =
    useNotifications();
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const filteredNotifications = (() => {
    const types = FILTER_TYPE_MAP[activeFilter];
    if (!types) return notifications;
    return notifications.filter((n) => (types as readonly string[]).includes(n.type));
  })();

  const handleNavigate = (route: string) => {
    setOpen(false);
    setExpanded(false);
    navigate(route);
  };

  const handleExecuteAction = (notificationId: string, action: NotificationAction) => {
    executeAction(notificationId, action);
    if (action.type === 'dismiss') {
      dismiss(notificationId);
    }
  };

  const handleExpandClick = () => {
    setOpen(false);
    setExpanded(true);
  };

  const handleBackToCompact = () => {
    setExpanded(false);
    setOpen(true);
  };

  const handleCloseExpanded = () => {
    setExpanded(false);
  };

  const totalCount = filteredNotifications.length;

  return (
    <>
      {/* Compact mode: Radix Popover */}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            data-tour="shell-notifications"
            aria-label={
              unreadCount > 0
                ? `Notifications, ${unreadCount} unread`
                : 'Notifications'
            }
            className="relative w-8 h-8 rounded-full bg-wayne-surface border border-wayne-hairline flex items-center justify-center hover:border-wayne-accent hover:scale-[1.08] active:scale-95 transition-all"
            style={{ transition: 'border-color 200ms var(--ease-default), transform 280ms var(--ease-bounce)' }}
          >
            <Bell size={16} className={`text-wayne-ink-2${unreadCount > 0 ? ' animate-[bellShake_0.5s_ease-in-out]' : ''}`} />
            {unreadCount > 0 ? (
              <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-wayne-red text-white text-[10px] font-semibold leading-none border-2 border-wayne-bg">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            ) : null}
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="end"
            sideOffset={8}
            className="w-[340px] max-h-[480px] flex flex-col rounded-xl bg-wayne-surface border border-wayne-hairline shadow-xl z-[9999] overflow-hidden animate-[modalIn_200ms_ease-out_both]"
            role="dialog"
            aria-label="Notifications"
          >
            {/* Header */}
            <div className="shrink-0 px-4 pt-4 pb-2">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-serif text-[16px] font-medium text-wayne-ink">
                  Notifications
                </h2>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 ? (
                    <button
                      type="button"
                      onClick={markAllRead}
                      className="text-[12px] text-wayne-ink-3 hover:text-wayne-accent transition-colors active:scale-[0.97]"
                      style={{ transition: 'color 150ms var(--ease-default)' }}
                    >
                      Mark all read
                    </button>
                  ) : null}
                  <Popover.Close asChild>
                    <button
                      type="button"
                      aria-label="Close notifications"
                      className="w-6 h-6 rounded-md flex items-center justify-center text-wayne-ink-3 hover:text-wayne-ink hover:bg-wayne-surface-2 transition-colors active:scale-[0.97]"
                    >
                      <X size={14} />
                    </button>
                  </Popover.Close>
                </div>
              </div>

              {/* Filter tabs */}
              <FilterBar activeFilter={activeFilter} onFilterChange={setActiveFilter} />
            </div>

            {/* Divider */}
            <div className="shrink-0 h-px bg-wayne-hairline" />

            {/* Notification list */}
            <div className="flex-1 overflow-y-auto">
              <GroupedNotificationList
                notifications={[...filteredNotifications]}
                expanded={false}
                onMarkRead={markRead}
                onExecuteAction={handleExecuteAction}
                onNavigate={handleNavigate}
              />
            </div>

            {/* Footer */}
            <div className="shrink-0 border-t border-wayne-hairline px-4 py-2.5 flex items-center justify-between">
              <button
                type="button"
                onClick={() => handleNavigate('/settings')}
                className="text-[12px] text-wayne-ink-3 hover:text-wayne-accent transition-colors active:scale-[0.97]"
                style={{ transition: 'color 150ms var(--ease-default)' }}
              >
                Notification settings
              </button>
              <button
                type="button"
                onClick={handleExpandClick}
                className="text-[12px] text-wayne-accent hover:text-wayne-accent-2 font-medium transition-colors active:scale-[0.97]"
                style={{ transition: 'color 150ms var(--ease-default)' }}
              >
                View all notifications
              </button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Expanded mode: Radix Dialog (full-screen modal) */}
      <Dialog.Root open={expanded} onOpenChange={(isOpen) => { if (!isOpen) handleCloseExpanded(); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 z-[9998] animate-[fadeIn_150ms_ease-out_both]" />
          <Dialog.Content
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[720px] max-h-[80vh] flex flex-col rounded-xl bg-wayne-surface border border-wayne-hairline shadow-2xl z-[9999] overflow-hidden animate-[modalIn_200ms_ease-out_both]"
            aria-label="All notifications"
          >
            {/* Header */}
            <div className="shrink-0 px-6 pt-5 pb-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleBackToCompact}
                    aria-label="Back to compact view"
                    className="w-7 h-7 rounded-md flex items-center justify-center text-wayne-ink-3 hover:text-wayne-ink hover:bg-wayne-surface-2 transition-all active:scale-[0.97]"
                    style={{ transition: 'all 150ms var(--ease-default)' }}
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <div>
                    <Dialog.Title className="font-serif text-[18px] font-medium text-wayne-ink">
                      Notifications
                    </Dialog.Title>
                    <p className="text-[12px] text-wayne-ink-3 mt-0.5">
                      {totalCount} notification{totalCount !== 1 ? 's' : ''} &middot; {unreadCount} unread
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 ? (
                    <button
                      type="button"
                      onClick={markAllRead}
                      className="text-[12px] text-wayne-ink-3 hover:text-wayne-accent transition-colors active:scale-[0.97]"
                      style={{ transition: 'color 150ms var(--ease-default)' }}
                    >
                      Mark all read
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={clearAllRead}
                    className="text-[12px] text-wayne-ink-3 hover:text-wayne-accent transition-colors active:scale-[0.97]"
                    style={{ transition: 'color 150ms var(--ease-default)' }}
                  >
                    Clear all
                  </button>
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      aria-label="Close notifications"
                      className="w-7 h-7 rounded-md flex items-center justify-center text-wayne-ink-3 hover:text-wayne-ink hover:bg-wayne-surface-2 transition-colors active:scale-[0.97]"
                    >
                      <X size={16} />
                    </button>
                  </Dialog.Close>
                </div>
              </div>

              {/* Filter tabs */}
              <FilterBar activeFilter={activeFilter} onFilterChange={setActiveFilter} />
            </div>

            {/* Divider */}
            <div className="shrink-0 h-px bg-wayne-hairline" />

            {/* Notification list (scrollable) */}
            <div className="flex-1 overflow-y-auto">
              <GroupedNotificationList
                notifications={[...filteredNotifications]}
                expanded={true}
                onMarkRead={markRead}
                onExecuteAction={handleExecuteAction}
                onNavigate={handleNavigate}
              />
            </div>

            {/* Footer */}
            <div className="shrink-0 border-t border-wayne-hairline px-6 py-3 flex items-center justify-between">
              <button
                type="button"
                onClick={() => handleNavigate('/settings')}
                className="text-[12px] text-wayne-ink-3 hover:text-wayne-accent transition-colors active:scale-[0.97]"
                style={{ transition: 'color 150ms var(--ease-default)' }}
              >
                Notification settings
              </button>
              <button
                type="button"
                onClick={handleBackToCompact}
                className="text-[12px] text-wayne-ink-2 hover:text-wayne-ink font-medium transition-colors active:scale-[0.97]"
                style={{ transition: 'color 150ms var(--ease-default)' }}
              >
                Back to compact
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export default NotificationBell;
